import React, { useState, useMemo, useContext, useCallback, CSSProperties } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled,
  TextField,
  InputAdornment,
  Box,
  CircularProgress,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SettingsIcon from '@mui/icons-material/Settings';
import * as Dayjs from 'dayjs'
import TableMenuButton from "components/button/TableMenuButton";
import SearchIcon from '@mui/icons-material/Search';
import { useDeleteProjectMutation, useGetProjectsQuery } from "api/api";
import { StyledTableCell, StyledTableRow, TableLoadingRow } from 'components/tables/styledTableComponents';
import { ProjectContext } from 'context/ProjectContext';
import { useLocation, useNavigate } from 'react-router-dom';
import ErrorModal from 'components/modal/ErrorModal';
import { getErrorMessage } from 'lib/utils/getErrorMessage';

const filterLabelStyles: CSSProperties = { width: '100%', height: '100%', color: '#555555', fontSize: 16, fontFamily: 'Helvetica Neue LT Std', fontWeight: '700', wordWrap: 'break-word', marginBottom: '10px' };

const downArrowPath = '/assets/icons/ArrowsDown.svg';
const upArrowPath = '/assets/icons/ArrowsUp.svg';
const upDownArrowPath = '/assets/icons/ArrowsDownUp.svg';

interface ProjectTableProps {
  isSuperAdmin: boolean;
}

interface ProjectTableFilters {
  searchTerm?: string | null
  startDate?: Dayjs.Dayjs | null,
  endDate?: Dayjs.Dayjs | null,
  status?: string | null
  creator?: string | null
}

const ProjectsTable: React.FC<ProjectTableProps> = ({ isSuperAdmin }) => {
  const location = useLocation();
  
  const [sortConfig, setSortConfig] = useState({ key: 'updated_at', direction: 'descending' });
  const [filters, setFilters] = useState<ProjectTableFilters>(() => {
    const params = new URLSearchParams(location.search);

    const initialFilters: ProjectTableFilters = {
      searchTerm: params.get('q') || '',
      status: params.get('status') || 'any',
      creator: params.get('creator') || 'any'
    };

    const dateRange = params.get('date_range');
    if (!dateRange) return initialFilters;

    const dateParts = dateRange.split('-');
    if (dateParts?.[0]) {
      let startDate = new Date(dateParts[0]);
      // @ts-ignore
      initialFilters.startDate = Dayjs(startDate);
    }
    if (dateParts?.[1]) {
      let endDate = new Date(dateParts[1]);
      // @ts-ignore
      initialFilters.endDate = Dayjs(endDate);
    }

    return initialFilters;
  });

  const [deleteProject, { error: projectDeleteError, reset: resetDeleteProject }] = useDeleteProjectMutation();

  const { data: projectsData = [], isLoading, refetch } = useGetProjectsQuery();
  const navigate = useNavigate();
  const context = useContext(ProjectContext);

  if (!context) {
    throw new Error('ProjectContext error');
  }

  const { setProjectId } = context;

  const handleEditProject = async (project: any) => {
    try {
      if (project.id && typeof project.id === 'number') {
        setProjectId(project.id);
        navigate(`/add-project/1`);
      }
    } catch (e) {
      console.error('ProjectsTable.tsx, handleEditProject error: ', e);
    }
  };

  const handleSortTable = (key: string) => {
    let direction = 'ascending';
    if (sortConfig.key === reformatKey(key) && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    const reformattedKey = reformatKey(key);
    setSortConfig({ key: reformattedKey, direction });
  };

  const getSortIcon = (key: string) => {
    const arrowUp = <img src={upArrowPath} alt="Sort Ascending" />;
    const arrowDown = <img src={downArrowPath} alt="Sort Descending" />;
    const arrowUpDown = <img src={upDownArrowPath} alt="Sort" />;

    if (sortConfig.key === reformatKey(key)) {
      return sortConfig.direction === 'ascending' ? arrowUp : arrowDown;
    }
    return arrowUpDown;
  };

  const reformatKey = (key: string) => {
    const keyMappings: { [key: string]: string } = {
      'Project Name': 'name',
      'Location': 'subnational',
      'Firm Name': 'firm',
      'Last Updated By': 'updated_by',
      'Last Updated': 'updated_at',
      'Submitted By': 'submitted_by',
      'Submitted': 'submitted_at',
      'Created By': 'created_by',
      'Created': 'created_at',
      'Primary Building Use Type': 'primary_building_use_type',
      'Construction Type': 'construction_type',
      'Construction Completion Year': 'construction_completion_year',
      'Gross Area (ft^2)': 'gross_area_sqft',
      'Total Embodied Carbon excluding D (kgCO2e)': 'total_gwp_kg_exclude_d',
      'Embodied Carbon Intensity excluding D (kg CO2e/m2)': 'total_intensity_exclude_d',
      'ECOM Version': 'ecom_version',
      'Submission Status': 'is_submitted',
    };
    return keyMappings[key] || key;
  };

  const searchIncludesProject = useCallback((item, searchTerm = '') => {
    if (item) {
      const isSubmittedText = item.is_submitted ? 'Submitted' : 'Draft';
      return (
        (item.name?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
        (item.firm?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
        (item.address?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
        (item.subnational?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
        (item.updated_by?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
        (item.updated_at?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
        isSubmittedText.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.submitted_by?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
        (item.submitted_at?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
        (item.created_by?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
        (item.created_at?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
        (item.primary_building_use_type?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
        (item.construction_type?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
        (item.construction_completion_year?.toString().toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
        (item.gross_area_sqft?.toString().toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
        (item.total_gwp_kg_exclude_d?.toString().toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
        (item.total_intensity_exclude_d?.toString().toLowerCase().includes(searchTerm.toLowerCase()) || '')
      );
    }
    return false;
  }, []);

  const filteredAndSortedData = useMemo(() => {
    let filteredItems = [ ...projectsData ];

    for (const [filterKey, filterValue] of Object.entries(filters)) {
      if (!filterKey || !filterValue) continue;

      switch (filterKey) {
        case 'searchTerm':
          if (!filterValue) continue;

          filteredItems = filteredItems.filter((item) => searchIncludesProject(item, filterValue));
          break;
        case 'startDate':
          const startTimestamp = filterValue.toDate().getTime();
          if (!startTimestamp) continue;

          filteredItems = filteredItems.filter(item => {
            if (!item.submitted_at) return false;
            const submitTimestamp = (new Date(item.submitted_at)).getTime();
            return submitTimestamp >= startTimestamp;
          });
          break;
        case 'endDate':
          const endTimestamp = filterValue.toDate().getTime();
          if (!endTimestamp) continue;

          filteredItems = filteredItems.filter(item => {
            if (!item.submitted_at) return false;
            const submitTimestamp = (new Date(item.submitted_at)).getTime();
            return submitTimestamp <= endTimestamp;
          });
          break;
        case 'status':
          if (!filterValue || filterValue === 'any') continue;

          const shouldBeSubmitted = filterValue === 'submitted';
          filteredItems = filteredItems.filter(item => item.is_submitted === shouldBeSubmitted);
          break;
        case 'creator':
          if (!filterValue || filterValue === 'any') continue;
          
          filteredItems = filteredItems.filter(item => item.created_by === filterValue);
          break;
      }
    }

    if (sortConfig !== null) {
      filteredItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // If they aren't the same type, convert them both to strings
        if (typeof aValue !== typeof bValue) {
          aValue = aValue ? aValue.toString() : '';
          bValue = bValue ? bValue.toString() : '';
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'ascending'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return filteredItems;
  }, [projectsData, sortConfig, filters]);

  const creatorOptions = useMemo(() => {
    let creatorSet = new Set<string>();
    projectsData.forEach(project => project.created_by && creatorSet.add(project.created_by));
    return Array.from(creatorSet).sort((a, b) => a.localeCompare(b))
  }, [projectsData]);

  const baseTableHeaders = [
    'Project Name',
    'Location',
    'Last Updated By',
    'Last Updated',
    'Submission Status',
    'Submitted By',
    'Submitted',
    'Created By',
    'Created',
    'Primary Building Use Type',
    'Construction Type',
    'Construction Completion Year',
    'Gross Area (ft^2)',
    'Total Embodied Carbon excluding D (kgCO2e)',
    'Embodied Carbon Intensity excluding D (kg CO2e/m2)',
    'ECOM Version'
  ];

  const tableHeaders = useMemo(() => {
    if (isSuperAdmin) {
      // Inserts Firm Name after Project Name
      return [
        ...baseTableHeaders.slice(0, 1),
        'Firm Name',
        ...baseTableHeaders.slice(1)
      ];
    }
    return baseTableHeaders;
  }, [isSuperAdmin]);

  return (
    <>
      <ErrorModal
        open={!!projectDeleteError}
        onClose={resetDeleteProject}
        errorMessage={projectDeleteError ? getErrorMessage(projectDeleteError) : null}
      />
      <div style={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        flexWrap: 'wrap',
        marginTop: '10px',
        columnGap: '16px',
        rowGap: '8px'
      }}>
        <Box sx={{
          position: 'relative'
        }}>
          <InputLabel style={filterLabelStyles}>
            Search
          </InputLabel>
          <TextField
            placeholder="Search..."
            defaultValue={filters.searchTerm || undefined}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLElement).blur();
            }}
            onBlur={(e) => setFilters(fs => ({ ...fs, searchTerm: e.target.value }))}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              style: {
                borderRadius: 22,
                height: "44px",
                width: "300px",
              }
            }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <InputLabel style={filterLabelStyles}>
            Created By
          </InputLabel>
          <FormControl>
            <Select
              value={filters.creator}
              onChange={(e) => setFilters(fs => ({ ...fs, creator: e.target.value }))}
              sx={{
                borderRadius: '22px',
                height: "44px",
                width: "200px",
                '.MuiInputBase-input': {
                  opacity: filters.creator === 'any' ? 0.42 : undefined,
                }
              }}
            >
              <MenuItem key={'any'} value={'any'}>Any</MenuItem>
              {creatorOptions.map((creator, ind) => <MenuItem key={`${ind}`} value={creator}>{creator}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <InputLabel style={filterLabelStyles}>
            Submission Status
          </InputLabel>
          <FormControl>
            <Select
              value={filters.status}
              onChange={(e) => setFilters(fs => ({ ...fs, status: e.target.value }))}
              sx={{
                borderRadius: '22px',
                height: "44px",
                width: "200px",
                '.MuiInputBase-input': {
                  opacity: filters.status === 'any' ? 0.42 : undefined,
                }
              }}
            >
              <MenuItem value={'any'}>Any</MenuItem>
              <MenuItem value={'submitted'}>Submitted</MenuItem>
              <MenuItem value={'draft'}>Draft</MenuItem>
            </Select>
          </FormControl>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <InputLabel style={filterLabelStyles}>
            Submitted After
          </InputLabel>
          <FormControl>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                sx={{
                  '.MuiInputBase-root': {
                    borderRadius: '22px',
                    height: "44px",
                    width: "200px",
                  }
                }}
                value={filters.startDate}
                slotProps={filters.startDate ? {
                  actionBar: {
                    actions: ['clear'],
                  },
                } : undefined}
                onChange={(newValue) => setFilters(fs => ({ ...fs, startDate: newValue }))}
              />
            </LocalizationProvider>
          </FormControl>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <InputLabel style={filterLabelStyles}>
            Submitted Before
          </InputLabel>
          <FormControl>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                sx={{
                  '.MuiInputBase-root': {
                    borderRadius: '22px',
                    height: "44px",
                    width: "200px",
                  }
                }}
                value={filters.endDate}
                slotProps={filters.endDate ? {
                  actionBar: {
                    actions: ['clear'],
                  },
                } : undefined}
                onChange={(newValue) => setFilters(fs => ({ ...fs, endDate: newValue }))}
              />
            </LocalizationProvider>
          </FormControl>
        </Box>
      </div>
      
      <TableContainer
        component={Paper}
        className="custom-scrollbar"
        sx={{
          mt: "20px",
          overflowX: 'auto',
          overflowY: 'auto',
          maxWidth: '100%',
          maxHeight: '60vh',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            backgroundImage: 'linear-gradient(90deg, #059BD6 0%, #9FD64D 100%)',
            zIndex: 101,
          }}
        />
        <Table stickyHeader>
          <TableHead>
            <StyledTableRow>
              {tableHeaders.map(key => (
                <StyledTableCell
                  key={key}
                  onClick={() => handleSortTable(key)}
                  className={sortConfig.key === key ? 'sorted' : ''}
                >
                  {key}
                  <span className="sort-icon">
                    {getSortIcon(key)}
                  </span>
                </StyledTableCell>
              ))}
              <StyledTableCell sx={{
                position: 'sticky !important',
                right: 0,
                zIndex: 100,
              }}>
                <SettingsIcon sx={{
                  display: 'flex',
                  alignSelf: 'center'
                }} />
              </StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {isLoading ?
              <TableLoadingRow colSpan={tableHeaders.length + 1} />
              :
              filteredAndSortedData.map((project, index) => (
                <StyledTableRow key={index}>
                  <StyledTableCell>{project.name}</StyledTableCell>
                  {isSuperAdmin && <StyledTableCell>{project.firm}</StyledTableCell>}
                  <StyledTableCell>
                    {[project.address, project.city, project.subnational, project.zipcode, project.country]
                      .filter(Boolean)
                      .join(', ')}
                  </StyledTableCell>
                  <StyledTableCell>{project.updated_by}</StyledTableCell>
                  <StyledTableCell>{project.updated_at}</StyledTableCell>
                  <StyledTableCell>{project.is_submitted ? "Submitted" : "Draft"}</StyledTableCell>
                  <StyledTableCell>{project.submitted_by}</StyledTableCell>
                  <StyledTableCell>{project.submitted_at}</StyledTableCell>
                  <StyledTableCell>{project.created_by}</StyledTableCell>
                  <StyledTableCell>{project.created_at}</StyledTableCell>
                  <StyledTableCell>{project.primary_building_use_type}</StyledTableCell>
                  <StyledTableCell>{project.construction_type}</StyledTableCell>
                  <StyledTableCell>{project.construction_completion_year}</StyledTableCell>
                  <StyledTableCell>{project.gross_area_sqft}</StyledTableCell>
                  <StyledTableCell>{project.total_gwp_kg_exclude_d}</StyledTableCell>
                  <StyledTableCell>{project.total_intensity_exclude_d}</StyledTableCell>
                  <StyledTableCell>{project.ECOM_version}</StyledTableCell>
                  <StyledTableCell style={{ overflow: 'visible', position: 'sticky', right: 0, padding: 0 }}>
                    <TableMenuButton
                      index={index}
                      tableContext="projects"
                      rowData={project}
                      onDelete={refetch}
                      deleteMutation={deleteProject}
                      onEdit={() => handleEditProject(project)}
                    />
                  </StyledTableCell>
                </StyledTableRow>
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default ProjectsTable;